import axios from 'axios';
import { Auth } from 'aws-amplify';
import config from '../config';

// Helper function to parse API response body if it's a string
const parseResponseBody = (response) => {
  if (!response) return null;
  
  let parsedBody = response.body;
  
  // If response has a body property that's a string, parse it
  if (response.body && typeof response.body === 'string') {
    try {
      parsedBody = JSON.parse(response.body);
    } catch (e) {
      console.error('Error parsing response body:', e);
      // If parsing fails, return response as is, but we might still want to check if body is useable
      return response;
    }
  }
  
  // If parsedBody is an object (either it was an object or we parsed it), merge it
  if (parsedBody && typeof parsedBody === 'object') {
    // Return the parsed body merged with other response properties
    // This ensures that 'success', 'data', 'error', etc. from the body are available at the top level
    return {
      ...response,
      ...parsedBody,
      // Ensure data field is preserved if it exists in parsedBody, otherwise keep original
      data: parsedBody.data !== undefined ? parsedBody.data : response.data
    };
  }
  
  return response;
};

// Enhanced helper function to extract data from deeply nested API responses
const extractFromNestedResponse = (response) => {
  if (!response) return null;
  
  // Handle the new test_results structure first
  if (response.body?.data?.test_results?.endpoint_response?.body) {
    try {
      const parsedBody = typeof response.body.data.test_results.endpoint_response.body === 'string' 
        ? JSON.parse(response.body.data.test_results.endpoint_response.body)
        : response.body.data.test_results.endpoint_response.body;
      
      return parsedBody;
    } catch (parseError) {
      console.warn('Failed to parse test_results body:', parseError);
    }
  }
  
  if (response.data?.test_results?.endpoint_response?.body) {
    try {
      const parsedBody = typeof response.data.test_results.endpoint_response.body === 'string' 
        ? JSON.parse(response.data.test_results.endpoint_response.body)
        : response.data.test_results.endpoint_response.body;
      
      return parsedBody;
    } catch (parseError) {
      console.warn('Failed to parse test_results body from data:', parseError);
    }
  }
  
  // Fallback to existing extraction logic
  return parseResponseBody(response);
};

export const makeAuthenticatedRequest = async (method, endpoint, payload = null, customHeaders = {}) => {
  try {
    const session = await Auth.currentSession();
    const idToken = session.getIdToken().getJwtToken();
    const accessToken = session.getAccessToken().getJwtToken();

    const requestUrl = `${config.api.endpoint}${endpoint}`;

    let finalPayload = payload;
    let authHeaderToken = idToken;  // Default: ID token for authorization

    if (endpoint.includes('/admin/cognito-admin')) {
      // Admin endpoint uses ID token (needs cognito:groups claim)
      finalPayload = {
        ...(payload || {}),
        auth_token: idToken
      };
    } else if (endpoint.includes('/cognito/cognito-user')) {
      // API Gateway authorizer validates the ID token in the header
      // But the Lambda needs the ACCESS token for user-level Cognito APIs
      finalPayload = {
        ...(payload || {}),
        auth_token: accessToken  // ← ACCESS token in body for Lambda to use
      };
      // authHeaderToken stays as idToken (default) for API Gateway auth
    }

    const response = await axios({
      method: method,
      url: requestUrl,
      headers: {
        'Authorization': authHeaderToken,  // ID token satisfies API Gateway authorizer
        'Content-Type': 'application/json',
        ...customHeaders
      },
      data: finalPayload,
      withCredentials: false,
      validateStatus: function (status) {
        return (status >= 200 && status < 300) || status === 400;
      }
    });

    const parsedResponse = extractFromNestedResponse(response.data);
    return parsedResponse;
  } catch (error) {
    console.error('API request error:', error);
    if (error.response && error.response.data) {
      const parsedResponse = extractFromNestedResponse(error.response.data);
      return parsedResponse;
    }
    throw error;
  }
};

// Unauthenticated request helper for public endpoints
const makeUnauthenticatedRequest = async (method, endpoint, payload = null) => {
  try {
    const requestUrl = `${config.api.endpoint}${endpoint}`;

    const response = await axios({
      method: method,
      url: requestUrl,
      headers: {
        'Content-Type': 'application/json'
      },
      data: payload,
      withCredentials: false,
      validateStatus: function (status) {
        return (status >= 200 && status < 300) || status === 400;
      }
    });

    const parsedResponse = extractFromNestedResponse(response.data);
    return parsedResponse;
  } catch (error) {
    console.error('API request error:', error);

    if (error.response && error.response.data) {
      const parsedResponse = extractFromNestedResponse(error.response.data);
      return parsedResponse;
    }

    throw error;
  }
};

// ENHANCED: Transformation function to include all environment-specific fields, CDK, and Python layers
const transformAgentFromNewToOld = (newAgent) => {
  return {
    uniqueIdentifier: String(newAgent.agent_id),
    agentName: newAgent.agent_name,
    agentDescription: newAgent.description,
    awsAccountNumber: newAgent.aws_account_prod,
    awsAccount: newAgent.aws_account_prod,
    awsRegion: newAgent.aws_region_prod,
    awsRoleForGithub: newAgent.aws_github_role,
    commissioned: newAgent.status === 'production',
    deployedInDev: newAgent.core_deployed_development,
    deployedInProd: newAgent.core_deployed_production,
    api_deployed_production: newAgent.api_deployed_production,
    api_deployed_development: newAgent.api_deployed_development,
    accessories_deployed_production: newAgent.accessories_deployed_production,
    accessories_deployed_development: newAgent.accessories_deployed_development,
    githubTokenSecretName: newAgent.github_token_secret_name,
    repoAccount: newAgent.github_account,
    repoName: newAgent.github_repo,
    
    // Config and infrastructure fields
    cdkQualifier: newAgent.cdk_qualifier,
    stackPrefix: newAgent.stack_prefix,
    buildProject: newAgent.build_project,
    backupBucket: newAgent.backup_bucket,
    
    // Python Layers
    llmLayerPythonLibraries: newAgent.llm_layer_python_libraries,
    apiLayerPythonLibraries: newAgent.api_layer_python_libraries,
    knowledgeLayerPythonLibraries: newAgent.knowledge_layer_python_libraries,

    // Step Functions
    stepFunctionsSharedRoleName: newAgent.step_functions_shared_role_name,
    stepFunctionsEnableSharedRole: newAgent.step_functions_enable_shared_role,
    
    // Development environment fields
    core_domain_dev: newAgent.core_domain_dev,
    core_subdomain_dev: newAgent.core_subdomain_dev,
    core_user_pool_id_dev: newAgent.core_user_pool_id_dev,
    core_user_pool_client_dev: newAgent.core_user_pool_client_dev,
    core_identity_pool_dev: newAgent.core_identity_pool_dev,
    api_domain_dev: newAgent.api_domain_dev,
    api_subdomain_dev: newAgent.api_subdomain_dev,
    api_user_pool_id_dev: newAgent.api_user_pool_id_dev,
    api_user_pool_client_dev: newAgent.api_user_pool_client_dev,
    api_identity_pool_dev: newAgent.api_identity_pool_dev,
    conversation_domain_dev: newAgent.conversation_domain_dev,
    conversation_subdomain_dev: newAgent.conversation_subdomain_dev,
    conversation_user_pool_id_dev: newAgent.conversation_user_pool_id_dev,
    conversation_user_pool_client_dev: newAgent.conversation_user_pool_client_dev,
    conversation_identity_pool_dev: newAgent.conversation_identity_pool_dev,
    api_test_dev_username: newAgent.api_test_dev_username,
    api_test_dev_password: newAgent.api_test_dev_password,
    
    // Production environment fields
    core_domain_prod: newAgent.core_domain_prod,
    core_subdomain_prod: newAgent.core_subdomain_prod,
    core_user_pool_id_prod: newAgent.core_user_pool_id_prod,
    core_user_pool_client_prod: newAgent.core_user_pool_client_prod,
    core_identity_pool_prod: newAgent.core_identity_pool_prod,
    api_domain_prod: newAgent.api_domain_prod,
    api_subdomain_prod: newAgent.api_subdomain_prod,
    api_user_pool_id_prod: newAgent.api_user_pool_id_prod,
    api_user_pool_client_prod: newAgent.api_user_pool_client_prod,
    api_identity_pool_prod: newAgent.api_identity_pool_prod,
    conversation_domain_prod: newAgent.conversation_domain_prod,
    conversation_subdomain_prod: newAgent.conversation_subdomain_prod,
    conversation_user_pool_id_prod: newAgent.conversation_user_pool_id_prod,
    conversation_user_pool_client_prod: newAgent.conversation_user_pool_client_prod,
    conversation_identity_pool_prod: newAgent.conversation_identity_pool_prod,
    api_test_prod_username: newAgent.api_test_prod_username,
    api_test_prod_password: newAgent.api_test_prod_password
  };
};

const transformMicroAgentFromNewToOld = (newMicroAgent, agentName) => {
  return {
    uniqueIdentifier: String(newMicroAgent.micro_agent_id),
    agentNumber: newMicroAgent.micro_agent_number,
    microAgentName: newMicroAgent.name,
    microAgentDescription: newMicroAgent.description,
    parentAgentName: agentName,
    commissioned: newMicroAgent.status === 'production',
    deployedInDev: newMicroAgent.deployed_development,
    deployedInProd: newMicroAgent.deployed_production,
    isMiniAgent: newMicroAgent.is_mini_agent,
    // Include new metadata fields for enhanced functionality
    objective: newMicroAgent.objective,
    scope: newMicroAgent.scope,
    documentation: newMicroAgent.documentation,
    schema: newMicroAgent.schema,
    status: newMicroAgent.status,
    human_notes: newMicroAgent.human_notes,
    lambda_functions: newMicroAgent.lambda_functions || [],
    prompts: newMicroAgent.prompts || [],
    recent_executions: newMicroAgent.recent_executions || [],
    micro_agent_children: newMicroAgent.micro_agent_children || [],
    micro_service_children: newMicroAgent.micro_service_children || [],
    // Preserve agent_id for resolving lookups later
    agent_id: newMicroAgent.agent_id
  };
};

const transformExecutionFromNewToOld = (newExecution) => {
  const calculateEndtime = (starttime, durationSeconds) => {
    const start = new Date(starttime);
    const end = new Date(start.getTime() + durationSeconds * 1000);
    return end.toISOString();
  };

  return {
    uniqueIdentifier: String(newExecution.micro_agent_execution_id),
    agentNumber: newExecution.micro_agent_number,
    duration: newExecution.duration_seconds,
    endtime: calculateEndtime(newExecution.created_at, newExecution.duration_seconds),
    executionArn: newExecution.execution_arn,
    microAgentIdentifier: String(newExecution.micro_agent_id),
    outcome: newExecution.outcome,
    payload: newExecution.payload,
    result: newExecution.agent_output,
    starttime: newExecution.created_at
  };
};

// Enhanced Cache for micro-agent lookups with new API support
class MicroAgentCache {
  constructor() {
    this.cache = new Map();
    this.agentCache = new Map();
    this.lastInitialized = null;
    this.initializePromise = null;
  }
  
  async initialize() {
    // Prevent multiple concurrent initializations
    if (this.initializePromise) {
      return this.initializePromise;
    }

    // Check if cache is still fresh (within last 5 minutes)
    if (this.lastInitialized && (Date.now() - this.lastInitialized) < 300000) {
      return;
    }

    this.initializePromise = this._doInitialize();
    return this.initializePromise;
  }

  async _doInitialize() {
    try {
      const response = await makeAuthenticatedRequest('POST', '/db/micro-agents', {});
      
      // Enhanced extraction for the new nested structure
      let cacheData = response;
      
      // Check if we have the test_results structure
      if (response.body?.data?.test_results?.endpoint_response?.body) {
        try {
          cacheData = typeof response.body.data.test_results.endpoint_response.body === 'string'
            ? JSON.parse(response.body.data.test_results.endpoint_response.body)
            : response.body.data.test_results.endpoint_response.body;
        } catch (parseError) {
          console.warn('Failed to parse test_results for cache:', parseError);
          cacheData = response;
        }
      }
      
      if (cacheData && cacheData.success && cacheData.data) {
        // Clear existing cache
        this.cache.clear();
        this.agentCache.clear();

        // Cache micro-agents
        if (cacheData.data.micro_agents) {
          cacheData.data.micro_agents.forEach(ma => {
            const key = `${ma.agent_id}:${ma.micro_agent_number}`;
            this.cache.set(key, ma);
            
            // Also cache by micro_agent_id for direct lookups
            this.cache.set(`id:${ma.micro_agent_id}`, ma);
          });
        }
        
        // Cache agents
        if (cacheData.data.agents) {
          cacheData.data.agents.forEach(agent => {
            this.agentCache.set(agent.agent_name, agent.agent_id);
            // Also cache by agent_id for reverse lookups
            this.agentCache.set(`id:${agent.agent_id}`, agent.agent_name);
            // Also cache by github_repo name for compatibility
            if (agent.github_repo) {
              this.agentCache.set(agent.github_repo, agent.agent_id);
            }
          });
        }
        
        this.lastInitialized = Date.now();
      }
    } catch (error) {
      console.error('Error initializing micro-agent cache:', error);
    } finally {
      this.initializePromise = null;
    }
  }
  
  getMicroAgentId(agentNumber, parentAgentName) {
    const agentId = this.agentCache.get(parentAgentName);
    if (!agentId) return null;
    
    const key = `${agentId}:${agentNumber}`;
    const microAgent = this.cache.get(key);
    return microAgent?.micro_agent_id || null;
  }
  
  getMicroAgent(agentNumber, parentAgentName) {
    const agentId = this.agentCache.get(parentAgentName);
    if (!agentId) return null;
    
    const key = `${agentId}:${agentNumber}`;
    return this.cache.get(key) || null;
  }
  
  getMicroAgentById(microAgentId) {
    return this.cache.get(`id:${microAgentId}`) || null;
  }
  
  getAgentIdByName(agentName) {
    return this.agentCache.get(agentName);
  }
  
  getAgentNameById(agentId) {
    return this.agentCache.get(`id:${agentId}`);
  }

  // Clear cache to force refresh
  invalidate() {
    this.cache.clear();
    this.agentCache.clear();
    this.lastInitialized = null;
    this.initializePromise = null;
  }
}

// Initialize cache
const microAgentCache = new MicroAgentCache();

// Get all agents - Enhanced for new API structure
export const getAgents = async () => {
  try {
    const response = await makeAuthenticatedRequest('POST', '/db/agents', {});
    
    // Enhanced extraction for the new nested structure
    let apiResponse = response;
    
    // Check if we have the test_results structure
    if (response.body?.data?.test_results?.endpoint_response?.body) {
      try {
        apiResponse = typeof response.body.data.test_results.endpoint_response.body === 'string'
          ? JSON.parse(response.body.data.test_results.endpoint_response.body)
          : response.body.data.test_results.endpoint_response.body;
      } catch (parseError) {
        console.warn('Failed to parse test_results for agents:', parseError);
        apiResponse = response;
      }
    }
    
    if (!apiResponse || !apiResponse.success) {
      throw new Error(apiResponse?.error?.message || 'Failed to fetch agents');
    }
    
    // Access agents from data.agents
    const agents = apiResponse.data?.agents || [];
    
    if (!Array.isArray(agents)) {
      console.error('Agents is not an array:', agents);
      throw new Error('Invalid agents data structure');
    }
    
    // Transform to old format for backward compatibility
    const transformedAgents = agents.map(transformAgentFromNewToOld);
    
    return {
      success: true,
      agents: transformedAgents,
      body: { agents: transformedAgents }
    };
  } catch (error) {
    console.error('Error getting agents:', error);
    throw error;
  }
};

// Get all parent agents (alias for getAgents)
export const getParentAgents = async () => {
  return await getAgents();
};

// Get micro services - Function for micro services
export const getMicroServices = async (agentId) => {
  try {
    console.log('Fetching micro-services from /db/micro-services...', { agentId });
    
    // Build request payload
    const payload = {
      agent_id: agentId || "1" // Default to AgentDeveloper
    };
    
    const response = await makeAuthenticatedRequest('POST', '/db/micro-services', payload);
    
    console.log('Raw micro-services response:', response);
    
    // Enhanced extraction for nested response
    let apiResponse = response;
    if (response.body?.data?.test_results?.endpoint_response?.body) {
      try {
        console.log('Extracting micro-services from test_results structure...');
        apiResponse = typeof response.body.data.test_results.endpoint_response.body === 'string'
          ? JSON.parse(response.body.data.test_results.endpoint_response.body)
          : response.body.data.test_results.endpoint_response.body;
      } catch (parseError) {
        console.warn('Failed to parse test_results for micro-services:', parseError);
        apiResponse = response;
      }
    }
    
    if (!apiResponse || !apiResponse.success) {
      throw new Error(apiResponse?.error?.message || 'Failed to fetch micro-services');
    }
    
    let microServices = apiResponse.data?.micro_services || [];
    
    console.log(`Fetched ${microServices.length} micro-services from API`);
    
    // Transform the micro-services to expected format
    const transformedMicroServices = microServices.map(ms => ({
      id: ms.micro_service_id,
      micro_service_id: ms.micro_service_id,
      name: ms.name,
      description: ms.description,
      endpoint: ms.endpoint,
      method: ms.method,
      deployed_development: ms.deployed_development,
      deployed_production: ms.deployed_production,
      devStatus: ms.deployed_development ? 'deployed' : 'not deployed',
      prodStatus: ms.deployed_production ? 'deployed' : 'not deployed',
      status: ms.status,
      active: ms.active,
      agent_id: ms.agent_id,
      agent_name: ms.agent_name,
      scope: ms.scope
    }));
    
    console.log(`Returning ${transformedMicroServices.length} transformed micro-services`);
    
    return {
      success: true,
      microServices: transformedMicroServices
    };
  } catch (error) {
    console.error('Error getting micro-services:', error);
    throw error;
  }
};

// Helper to resolve agent ID from name or uniqueIdentifier
const resolveAgentId = async (parentAgentNameOrId) => {
  if (!parentAgentNameOrId) {
    return null;
  }
  
  try {
    // Initialize cache if needed
    await microAgentCache.initialize();
    
    // Check cache first by name
    const cachedId = microAgentCache.getAgentIdByName(parentAgentNameOrId);
    if (cachedId) {
      return cachedId;
    }
    
    // Fetch from API if not in cache
    const agentsResponse = await makeAuthenticatedRequest('POST', '/db/agents', {});
    
    // Enhanced extraction for nested response
    let extractedData = agentsResponse;
    if (agentsResponse.body?.data?.test_results?.endpoint_response?.body) {
      try {
        extractedData = typeof agentsResponse.body.data.test_results.endpoint_response.body === 'string'
          ? JSON.parse(agentsResponse.body.data.test_results.endpoint_response.body)
          : agentsResponse.body.data.test_results.endpoint_response.body;
      } catch (parseError) {
        extractedData = agentsResponse;
      }
    }
    
    if (extractedData?.success && extractedData.data?.agents) {
      const agent = extractedData.data.agents.find(a => 
        a.agent_name === parentAgentNameOrId || 
        a.github_repo === parentAgentNameOrId ||
        String(a.agent_id) === String(parentAgentNameOrId)
      );
      if (agent) {
        return agent.agent_id;
      }
    }
  } catch (err) {
    console.warn('Could not resolve agent ID:', err);
  }
  
  return null;
};

// Get micro agents - Enhanced for new API structure with client-side filtering
export const getMicroAgents = async (parentAgentName, filters = {}) => {
  try {
    // Initialize cache if needed
    await microAgentCache.initialize();
    
    // Build request payload
    const payload = {};
    
    // If we have a parent agent name, resolve it to an agent_id
    if (parentAgentName) {
      const agentId = await resolveAgentId(parentAgentName);
      if (agentId) {
        payload.agent_id = agentId;
      }
    }
    
    const response = await makeAuthenticatedRequest('POST', '/db/micro-agents', payload);
    
    // Enhanced extraction for nested response
    let apiResponse = response;
    if (response.body?.data?.test_results?.endpoint_response?.body) {
      try {
        apiResponse = typeof response.body.data.test_results.endpoint_response.body === 'string'
          ? JSON.parse(response.body.data.test_results.endpoint_response.body)
          : response.body.data.test_results.endpoint_response.body;
      } catch (parseError) {
        console.warn('Failed to parse test_results for micro-agents:', parseError);
        apiResponse = response;
      }
    }
    
    if (!apiResponse || !apiResponse.success) {
      throw new Error(apiResponse?.error?.message || 'Failed to fetch micro-agents');
    }
    
    let microAgents = apiResponse.data?.micro_agents || [];
    const agents = apiResponse.data?.agents || [];
    
    // Apply additional filters client-side
    if (filters.agentNumber) {
      microAgents = microAgents.filter(ma => 
        ma.micro_agent_number === filters.agentNumber
      );
    }
    
    if (filters.isMiniAgent !== undefined) {
      microAgents = microAgents.filter(ma => 
        ma.is_mini_agent === filters.isMiniAgent
      );
    }
    
    if (filters.commissioned !== undefined) {
      const targetStatus = filters.commissioned ? 'production' : 'development';
      microAgents = microAgents.filter(ma => ma.status === targetStatus);
    }
    
    if (filters.deployedInDev !== undefined) {
      microAgents = microAgents.filter(ma => 
        ma.deployed_development === filters.deployedInDev
      );
    }
    
    if (filters.deployedInProd !== undefined) {
      microAgents = microAgents.filter(ma => 
        ma.deployed_production === filters.deployedInProd
      );
    }
    
    if (filters.uniqueIdentifier) {
      microAgents = microAgents.filter(ma => 
        String(ma.micro_agent_id) === String(filters.uniqueIdentifier)
      );
    }
    
    // Transform to old format for compatibility
    const transformedMicroAgents = microAgents.map(ma => {
      const agent = agents.find(a => a.agent_id === ma.agent_id);
      return transformMicroAgentFromNewToOld(ma, agent?.agent_name || parentAgentName || '');
    });
    
    return {
      success: true,
      microAgents: transformedMicroAgents
    };
  } catch (error) {
    console.error('Error getting micro-agents:', error);
    throw error;
  }
};

// Get mini agents (micro agents with isMiniAgent = true)
export const getMiniAgents = async (parentAgentName) => {
  return await getMicroAgents(parentAgentName, { isMiniAgent: true });
};

// Enhanced getMicroAgentsbyId — no longer hardcodes agent_id: "1"
export const getMicroAgentsbyId = async (uniqueIdentifier, parentAgentIdHint = null) => {
  try {
    // Initialize cache if needed
    await microAgentCache.initialize();
    
    // Build payload — use the hint if provided, otherwise omit agent_id to search across all
    const payload = {
      micro_agent_id: uniqueIdentifier
    };
    
    // If we have a parent agent ID hint, include it to narrow the search
    if (parentAgentIdHint) {
      payload.agent_id = parentAgentIdHint;
      console.log(`getMicroAgentsbyId: Using agent_id hint: ${parentAgentIdHint} for micro_agent_id: ${uniqueIdentifier}`);
    } else {
      // Try to get the agent_id from cache
      const cachedMicroAgent = microAgentCache.getMicroAgentById(uniqueIdentifier);
      if (cachedMicroAgent && cachedMicroAgent.agent_id) {
        payload.agent_id = cachedMicroAgent.agent_id;
        console.log(`getMicroAgentsbyId: Resolved agent_id from cache: ${cachedMicroAgent.agent_id} for micro_agent_id: ${uniqueIdentifier}`);
      } else {
        console.log(`getMicroAgentsbyId: No agent_id hint or cache hit, fetching micro_agent_id: ${uniqueIdentifier} without agent_id filter`);
      }
    }
    
    const response = await makeAuthenticatedRequest('POST', '/db/micro-agents', payload);
    
    // Enhanced extraction for nested response
    let apiResponse = response;
    if (response.body?.data?.test_results?.endpoint_response?.body) {
      try {
        apiResponse = typeof response.body.data.test_results.endpoint_response.body === 'string'
          ? JSON.parse(response.body.data.test_results.endpoint_response.body)
          : response.body.data.test_results.endpoint_response.body;
      } catch (parseError) {
        console.warn('Failed to parse test_results for micro-agent by ID:', parseError);
        apiResponse = response;
      }
    }
    
    if (!apiResponse || !apiResponse.success) {
      throw new Error(apiResponse?.error?.message || 'Failed to fetch micro-agents');
    }
    
    const microAgents = apiResponse.data?.micro_agents || [];
    const agents = apiResponse.data?.agents || [];
    
    // Find the specific micro-agent by ID
    const microAgent = microAgents.find(ma => 
      String(ma.micro_agent_id) === String(uniqueIdentifier)
    );
    
    if (!microAgent) {
      console.warn(`Micro-agent with ID ${uniqueIdentifier} not found`);
      return {
        success: true,
        microAgents: []
      };
    }
    
    // Transform to old format
    const agent = agents.find(a => a.agent_id === microAgent.agent_id);
    const transformed = transformMicroAgentFromNewToOld(microAgent, agent?.agent_name || '');
    
    return {
      success: true,
      microAgents: [transformed]
    };
  } catch (error) {
    console.error(`Error getting micro agent with ID ${uniqueIdentifier}:`, error);
    throw error;
  }
};

// Helper function to get micro-agents for specific agent
export const getMicroAgentsByAgentName = async (agentName) => {
  return getMicroAgents(agentName);
};

// Helper function to get single micro-agent by number and parent
export const getMicroAgentByNumber = async (agentNumber, parentAgentName) => {
  const results = await getMicroAgents(parentAgentName, { agentNumber });
  return results.microAgents?.[0] || null;
};

// Get executions - NEW API with micro_agent_id
const getExecutions = async (microAgentId, outcomeFilter = null) => {
  try {
    if (!microAgentId) {
      throw new Error('Micro-agent ID is required to fetch executions');
    }
    
    const response = await makeAuthenticatedRequest('POST', '/db/micro-agent-executions', {
      micro_agent_id: microAgentId
    });
    
    if (!response || !response.success) {
      throw new Error(response?.error?.message || 'Failed to fetch executions');
    }
    
    let executions = response.data?.executions || [];
    
    // Apply outcome filter client-side if needed
    if (outcomeFilter) {
      executions = executions.filter(ex => ex.outcome === outcomeFilter);
    }
    
    // Transform to old format
    return executions.map(transformExecutionFromNewToOld);
    
  } catch (error) {
    console.error('Error fetching executions:', error);
    throw error;
  }
};

// Migration wrapper for executions - converts old parameters to new
export const getExecutionsByAgentDetails = async (agentNumber, parentAgentName, outcome = null) => {
  try {
    // Initialize cache if needed
    await microAgentCache.initialize();
    
    // Get micro_agent_id from agentNumber + parentAgentName
    const microAgentId = microAgentCache.getMicroAgentId(agentNumber, parentAgentName);
    
    if (!microAgentId) {
      // Try to find it by fetching all micro-agents
      const allMicroAgents = await getMicroAgents(parentAgentName, { agentNumber });
      if (allMicroAgents.microAgents && allMicroAgents.microAgents.length > 0) {
        const foundAgent = allMicroAgents.microAgents[0];
        
        // Convert uniqueIdentifier back to micro_agent_id for new API
        return getExecutions(foundAgent.uniqueIdentifier, outcome);
      }
      
      throw new Error(`Micro-agent not found: ${agentNumber} in ${parentAgentName}`);
    }
    
    // Use the new API with micro_agent_id
    return getExecutions(microAgentId, outcome);
  } catch (error) {
    console.error('Error getting executions by agent details:', error);
    throw error;
  }
};

// Get single execution by ARN
export const getExecutionByArn = async (executionArn) => {
  try {
    console.log(`Fetching execution by ARN: ${executionArn}`);
    
    const response = await makeAuthenticatedRequest('POST', '/db/micro-agent-executions', {
      execution_arn: executionArn
    });
    
    if (!response || !response.success) {
      if (response?.error?.code === 'NOT_FOUND') {
        console.log('Execution not found by ARN');
        return null;
      }
      throw new Error(response?.error?.message || 'Failed to fetch execution');
    }
    
    return transformExecutionFromNewToOld(response.data);
    
  } catch (error) {
    console.error('Error fetching execution by ARN:', error);
    throw error;
  }
};

// Create a new agent
export const createAgent = async (agentData) => {
  try {
    return await makeAuthenticatedRequest('POST', '/agents', agentData);
  } catch (error) {
    console.error('Error creating agent:', error);
    throw error;
  }
};

// Create a new parent agent
export const createParentAgent = async (agentData) => {
  try {
    return await makeAuthenticatedRequest('POST', '/parent-agents', agentData);
  } catch (error) {
    console.error('Error creating parent agent:', error);
    throw error;
  }
};

// Get agent by ID
export const getAgentById = async (agentId) => {
  try {
    const response = await getAgents();
    
    if (response && response.agents) {
      const agent = response.agents.find(a => 
        a.uniqueIdentifier === agentId || a.id === agentId
      );
      
      if (agent) {
        return {
          data: agent,
          body: { agents: [agent] }
        };
      }
    }
    
    return { data: null, body: { agents: [] } };
  } catch (error) {
    console.error(`Error getting agent with ID ${agentId}:`, error);
    throw error;
  }
};

// Update an existing agent
export const updateAgent = async (agentId, agentData) => {
  try {
    return await makeAuthenticatedRequest('PUT', `/agents/${agentId}`, agentData);
  } catch (error) {
    console.error(`Error updating agent with ID ${agentId}:`, error);
    throw error;
  }
};

// Delete an agent
export const deleteAgent = async (agentId) => {
  try {
    return await makeAuthenticatedRequest('DELETE', `/agents/${agentId}`);
  } catch (error) {
    console.error(`Error deleting agent with ID ${agentId}:`, error);
    throw error;
  }
};

// Enhanced: Helper to resolve parent agent from agentId parameter (handles uniqueIdentifier, agentName, or repoName)
const resolveParentAgent = async (agentId) => {
  try {
    const agentsResponse = await getAgents();
    let agentsList = [];
    if (agentsResponse && agentsResponse.agents) {
      agentsList = agentsResponse.agents;
    }
    
    const parentAgent = agentsList.find(a => 
      a.uniqueIdentifier === agentId || 
      a.agentName === agentId || 
      a.repoName === agentId
    );
    
    return parentAgent || null;
  } catch (err) {
    console.error('Error resolving parent agent:', err);
    return null;
  }
};

// Enhanced: Helper to resolve micro-agent number from targetId, using the correct parent agent context
const resolveMicroAgentNumber = async (targetId, parentAgentId = null) => {
  const microResponse = await getMicroAgentsbyId(targetId, parentAgentId);
  
  if (microResponse && microResponse.microAgents && microResponse.microAgents.length > 0) {
    return microResponse.microAgents[0].agentNumber;
  }
  
  throw new Error(`Could not find agent number for micro-agent ${targetId}`);
};

// Enhanced execution logs fetching with new API support
export const fetchExecutionLogs = async (agentId, targetType, executionArn, environment = 'production') => {
  try {
    console.log(`Fetching execution logs for: ${executionArn}, agent: ${agentId}, type: ${targetType}, env: ${environment}`);
    
    const parentAgent = await resolveParentAgent(agentId);
    
    if (typeof executionArn === 'string' && (executionArn.startsWith('arn:') || executionArn.includes(':'))) {
      // Try to get execution details from new API first
      try {
        const execution = await getExecutionByArn(executionArn);
        
        if (!execution) {
          console.warn('Execution not found in new API, falling back to old method');
          throw new Error('Execution not found');
        }
        
        console.log('Found execution in new API:', execution);
        
        const logPayload = {
          parentAgentName: parentAgent?.agentName || parentAgent?.repoName || agentId,
          awsAccount: parentAgent?.awsAccountNumber || config.aws.defaultAccount,
          awsRegion: parentAgent?.awsRegion || config.aws.defaultRegion,
          executionArn: executionArn,
          agentNumber: execution.agentNumber,
          environment: environment
        };
        
        const endpoint = `/logs`;
        const response = await makeAuthenticatedRequest('POST', endpoint, logPayload);
        
        return response;
      } catch (newApiError) {
        console.warn('New API failed for execution logs, falling back to old method:', newApiError);
        
        // Fallback to old method if new API fails
        const logPayload = {
          parentAgentName: parentAgent?.agentName || parentAgent?.repoName || agentId,
          awsAccount: parentAgent?.awsAccountNumber || config.aws.defaultAccount,
          awsRegion: parentAgent?.awsRegion || config.aws.defaultRegion,
          executionArn: executionArn
        };
        
        const endpoint = `/logs`;
        const response = await makeAuthenticatedRequest('POST', endpoint, logPayload);
        
        return response;
      }
    } else {
      let agentNumber = '';
      if (typeof targetType === 'string' && targetType.length === 3 && /^\d{3}$/.test(targetType)) {
        agentNumber = targetType;
      } else {
        agentNumber = await resolveMicroAgentNumber(targetType, parentAgent?.uniqueIdentifier);
      }
      
      const parentAgentName = parentAgent?.agentName || parentAgent?.repoName || agentId;
      const executions = await getMicroAgents(parentAgentName, { agentNumber });
      
      return {
        success: true,
        executions: executions.microAgents || []
      };
    }
  } catch (error) {
    console.error(`Error fetching execution logs: ${error.message}`);
    throw error;
  }
};

// Deploy an agent
export const deployAgent = async (agentId) => {
  try {
    return await makeAuthenticatedRequest('POST', `/agents/${agentId}/deploy`);
  } catch (error) {
    console.error(`Error deploying agent with ID ${agentId}:`, error);
    throw error;
  }
};

// Get schema — fixed to use resolved parent agent ID for micro-agent lookup
export const getSchema = async (agentId, targetType, targetId, environment = 'production') => {
  try {
    const parentAgent = await resolveParentAgent(agentId);
    
    if (!parentAgent) {
      console.error(`Parent agent not found for agentId: ${agentId}`);
      throw new Error(`Parent agent not found: ${agentId}`);
    }
    
    const agentNumber = await resolveMicroAgentNumber(targetId, parentAgent.uniqueIdentifier);
    
    const payload = {
      parentAgentName: parentAgent.agentName || parentAgent.repoName || agentId,
      repository: parentAgent.repoName || config.agent.defaultRepoName,
      githubAccount: parentAgent.repoAccount || config.github.defaultAccount,
      githubSecretName: config.github.defaultTokenSecretName,
      agentNumber: agentNumber,
      awsRegion: parentAgent.awsRegion || config.aws.defaultRegion,
      environment: environment
    };
    
    const endpoint = `/schema/${agentNumber}`;
    const response = await makeAuthenticatedRequest('POST', endpoint, payload);
    
    if (response && response.schema) {
      if (response.schema.input) {
        return response.schema.input;
      } else {
        return response.schema;
      }
    }
    
    return response;
  } catch (error) {
    console.error(`Error getting schema for agent ${targetId}:`, error);
    throw error;
  }
};

// Test agent — fixed to use resolved parent agent ID for micro-agent lookup
export const testAgent = async (agentId, targetType, targetId, testInput, environment = 'production') => {
  try {
    const parentAgent = await resolveParentAgent(agentId);
    
    if (!parentAgent) {
      console.error(`Parent agent not found for agentId: ${agentId}`);
      throw new Error(`Parent agent not found: ${agentId}`);
    }
    
    const agentNumber = await resolveMicroAgentNumber(targetId, parentAgent.uniqueIdentifier);
    
    const payload = {
      awsRegion: parentAgent.awsRegion || config.aws.defaultRegion,
      awsAccountNumber: parentAgent.awsAccountNumber || config.aws.defaultAccount,
      parentAgentName: parentAgent.agentName || parentAgent.repoName,
      agentNumber: agentNumber,
      environment: environment,
      payload: testInput
    };

    return await makeAuthenticatedRequest('POST', `/agents/${agentNumber}`, payload);
  } catch (error) {
    console.error(`Error testing agent with ID ${targetId}:`, error);
    throw error;
  }
};

// Get environment variables — fixed to use resolved parent agent ID for micro-agent lookup
export const getEnvironmentVariables = async (agentId, targetType, targetId, functionPath) => {
  try {
    const parentAgent = await resolveParentAgent(agentId);
    
    if (!parentAgent) {
      console.error(`Parent agent not found for agentId: ${agentId}`);
      throw new Error(`Parent agent not found: ${agentId}`);
    }
    
    const agentNumber = await resolveMicroAgentNumber(targetId, parentAgent.uniqueIdentifier);
    
    console.log(`getEnvironmentVariables: Resolved agentNumber=${agentNumber} for targetId=${targetId} under parentAgent=${parentAgent.agentName}`);
    
    const payload = {
      parentAgentName: parentAgent.agentName || parentAgent.repoName || agentId,
      repository: parentAgent.repoName || config.agent.defaultRepoName,
      githubAccount: parentAgent.repoAccount || config.github.defaultAccount,
      agentNumber: agentNumber,
      githubSecretName: config.github.defaultTokenSecretName,
      awsRegion: parentAgent.awsRegion || config.aws.defaultRegion
    };
    
    const endpoint = `/environment/${agentNumber}`;
    
    if (functionPath) {
      payload.functionPath = functionPath;
    }
    
    const response = await makeAuthenticatedRequest('POST', endpoint, payload);
    
    return response;
  } catch (error) {
    console.error(`Error getting environment variables for agent ${targetId}:`, error);
    throw error;
  }
};

// Update environment variables for an agent — fixed to use resolved parent agent ID
export const updateEnvironmentVariables = async (agentId, targetType, targetId, functionPath, variables) => {
  try {
    const parentAgent = await resolveParentAgent(agentId);
    
    if (!parentAgent) {
      console.error(`Parent agent not found for agentId: ${agentId}`);
      throw new Error(`Parent agent not found: ${agentId}`);
    }
    
    const agentNumber = await resolveMicroAgentNumber(targetId, parentAgent.uniqueIdentifier);
    
    console.log(`updateEnvironmentVariables: Resolved agentNumber=${agentNumber} for targetId=${targetId} under parentAgent=${parentAgent.agentName}`);
    
    const payload = {
      parentAgentName: parentAgent.agentName || parentAgent.repoName || agentId,
      repository: parentAgent.repoName || config.agent.defaultRepoName,
      githubAccount: parentAgent.repoAccount || config.github.defaultAccount,
      microAgent: agentNumber,
      environmentVariables: {
        [functionPath]: variables
      },
      updateAWS: true,
      githubSecretName: config.github.defaultTokenSecretName,
      awsRegion: parentAgent.awsRegion || config.aws.defaultRegion
    };
    
    return await makeAuthenticatedRequest('PUT', `/environment/${agentNumber}`, payload);
  } catch (error) {
    console.error(`Error updating environment variables for agent ${targetId}:`, error);
    throw error;
  }
};

// Get prompt content for an agent — FIXED: resolves correct parent agent and micro-agent mapping
export const getPromptContent = async (agentId, targetType, targetId, promptPath) => {
  try {
    const parentAgent = await resolveParentAgent(agentId);
    
    if (!parentAgent) {
      console.error(`Parent agent not found for agentId: ${agentId}`);
      throw new Error(`Parent agent not found: ${agentId}`);
    }
    
    // Use the resolved parent agent's uniqueIdentifier to narrow the micro-agent lookup
    const agentNumber = await resolveMicroAgentNumber(targetId, parentAgent.uniqueIdentifier);
    
    console.log(`getPromptContent: Resolved agentNumber=${agentNumber} for targetId=${targetId} under parentAgent=${parentAgent.agentName} (id=${parentAgent.uniqueIdentifier})`);
    
    const payload = {
      parentAgentName: parentAgent.agentName || parentAgent.repoName || agentId,
      repository: parentAgent.repoName || config.agent.defaultRepoName,
      githubAccount: parentAgent.repoAccount || config.github.defaultAccount,
      agentNumber: agentNumber,
      githubSecretName: config.github.defaultTokenSecretName,
      awsRegion: parentAgent.awsRegion || config.aws.defaultRegion
    };
    
    if (promptPath) {
      payload.promptPath = promptPath;
    }
    
    const endpoint = `/prompts/${agentNumber}`;
    console.log(`getPromptContent: Fetching from ${endpoint} with payload:`, payload);
    const response = await makeAuthenticatedRequest('POST', endpoint, payload);
    
    return response;
  } catch (error) {
    console.error(`Error getting prompt content for agent ${targetId}:`, error);
    throw error;
  }
};

// Update prompt content for an agent — fixed to use resolved parent agent ID
export const updatePromptContent = async (agentId, targetType, targetId, promptPath, content) => {
  try {
    const parentAgent = await resolveParentAgent(agentId);
    
    if (!parentAgent) {
      console.error(`Parent agent not found for agentId: ${agentId}`);
      throw new Error(`Parent agent not found: ${agentId}`);
    }
    
    const agentNumber = await resolveMicroAgentNumber(targetId, parentAgent.uniqueIdentifier);
    
    console.log(`updatePromptContent: Resolved agentNumber=${agentNumber} for targetId=${targetId} under parentAgent=${parentAgent.agentName}`);
    
    const payload = {
      parentAgentName: parentAgent.agentName || parentAgent.repoName || agentId,
      repository: parentAgent.repoName || config.agent.defaultRepoName,
      githubAccount: parentAgent.repoAccount || config.github.defaultAccount,
      microAgent: agentNumber,
      prompts: {
        [promptPath]: content
      },
      updateS3: true,
      githubSecretName: config.github.defaultTokenSecretName,
      awsRegion: parentAgent.awsRegion || config.aws.defaultRegion
    };
    
    return await makeAuthenticatedRequest('PUT', `/prompts/${agentNumber}`, payload);
  } catch (error) {
    console.error(`Error updating prompt content for agent ${targetId}:`, error);
    throw error;
  }
};

// Get agent configuration
export const getAgentConfig = async (parentAgentName, configType = 'overall') => {
  try {
    return await makeAuthenticatedRequest('POST', '/config', {
      parentAgentName: parentAgentName,
      repository: parentAgentName
    });
  } catch (error) {
    console.error(`Error getting configuration for agent ${parentAgentName}:`, error);
    throw error;
  }
};

// Update agent configuration
export const updateAgentConfig = async (configData) => {
  try {
    return await makeAuthenticatedRequest('PUT', '/config', configData);
  } catch (error) {
    console.error('Error updating agent configuration:', error);
    throw error;
  }
};

// Save agent deployment
export const saveAgentDeployment = async (agentId, deploymentData) => {
  try {
    return await makeAuthenticatedRequest('POST', `/deploy`, {
      repository: agentId,
      ...deploymentData
    });
  } catch (error) {
    console.error(`Error deploying agent ${agentId}:`, error);
    throw error;
  }
};

// Create a new micro-agent
export const createMicroAgent = async (microAgentData) => {
  try {
    return await makeAuthenticatedRequest('PUT', '/table/microagents', {
      operation: "create",
      tableName: "prod-agentdeveloper-micro-agents",
      awsRegion: config.aws.defaultRegion,
      githubAccount: config.github.defaultAccount,
      repository: config.agent.defaultRepoName,
      githubSecretName: config.github.defaultTokenSecretName,
      githubFilePath: "infrastructure/data/micro-agents.json",
      data: {
        agentNumber: microAgentData.agentNumber,
        microAgentName: microAgentData.name,
        parentAgentName: microAgentData.parentAgentName,
        microAgentDescription: microAgentData.description,
        deployedInDev: microAgentData.deployedInDev,
        deployedInProd: microAgentData.deployedInProd,
        commissioned: microAgentData.commissioned,
        isMiniAgent: microAgentData.isMiniAgent
      }
    });
  } catch (error) {
    console.error('Error creating micro-agent:', error);
    throw error;
  }
};

// Update an existing micro-agent
export const updateMicroAgent = async (uniqueId, microAgentData) => {
  try {
    return await makeAuthenticatedRequest('PUT', '/table/microagents', {
      operation: "update",
      tableName: "prod-agentdeveloper-micro-agents",
      awsRegion: config.aws.defaultRegion,
      githubAccount: config.github.defaultAccount,
      repository: config.agent.defaultRepoName,
      githubSecretName: config.github.defaultTokenSecretName,
      githubFilePath: "infrastructure/data/micro-agents.json",
      uniqueIdentifier: uniqueId,
      data: {
        uniqueIdentifier: uniqueId,
        agentNumber: microAgentData.agentNumber,
        microAgentName: microAgentData.name,
        parentAgentName: microAgentData.parentAgentName,
        microAgentDescription: microAgentData.description,
        deployedInDev: microAgentData.deployedInDev,
        deployedInProd: microAgentData.deployedInProd,
        commissioned: microAgentData.commissioned,
        isMiniAgent: microAgentData.isMiniAgent
      }
    });
  } catch (error) {
    console.error(`Error updating micro-agent with ID ${uniqueId}:`, error);
    throw error;
  }
};

// Update mini agent configuration
export const updateMiniAgentConfig = async (agentNumber, configData) => {
  try {
    return await makeAuthenticatedRequest('PUT', `/mini-config/${agentNumber}`, configData);
  } catch (error) {
    console.error(`Error updating mini agent config for agent ${agentNumber}:`, error);
    throw error;
  }
};

// Delete a micro-agent
export const deleteMicroAgent = async (uniqueId) => {
  try {
    return await makeAuthenticatedRequest('PUT', '/table/microagents', {
      operation: "delete",
      tableName: "prod-agentdeveloper-micro-agents",
      awsRegion: config.aws.defaultRegion,
      githubAccount: config.github.defaultAccount,
      repository: config.agent.defaultRepoName,
      githubSecretName: config.github.defaultTokenSecretName,
      githubFilePath: "infrastructure/data/micro-agents.json",
      uniqueIdentifier: uniqueId
    });
  } catch (error) {
    console.error(`Error deleting micro-agent with ID ${uniqueId}:`, error);
    throw error;
  }
};

// Update microagent list from database
export const updateMicroagentList = async () => {
  try {
    const response = await makeAuthenticatedRequest('POST', '/table/microagents/update', {
      body: {
        executionOnly: "False"
      }
    });
    
    return response;
  } catch (error) {
    console.error('Error updating microagent list:', error);
    throw error;
  }
};

// Update execution logs only
export const updateExecutionLogs = async () => {
  try {
    const response = await makeAuthenticatedRequest('POST', '/table/microagents/update', {
      body: {
        executionOnly: "True"
      }
    });
    
    return response;
  } catch (error) {
    console.error('Error updating execution logs:', error);
    throw error;
  }
};

// --- Knowledge Base API Functions ---

// Cluster Operations
export const listClusters = async (agentId, options = {}) => {
  const payload = {
    operation: 'list_clusters',
    agent_id: parseInt(agentId),
    ...options
  };
  return await makeAuthenticatedRequest('POST', '/knowledge/cluster_query', payload);
};

export const getCluster = async (clusterId) => {
  return await makeAuthenticatedRequest('POST', '/knowledge/cluster_query', {
    operation: 'get_cluster',
    cluster_id: clusterId,
    include_members: true
  });
};

export const getClusterStats = async (agentId) => {
  return await makeAuthenticatedRequest('POST', '/knowledge/cluster_query', {
    operation: 'get_stats',
    agent_id: parseInt(agentId)
  });
};

export const getClusterHierarchy = async (clusterId) => {
  return await makeAuthenticatedRequest('POST', '/knowledge/cluster_query', {
    operation: 'get_hierarchy',
    cluster_id: clusterId
  });
};

// Knowledge Query Operations
export const searchKnowledge = async (filters, pagination) => {
  return await makeAuthenticatedRequest('POST', '/knowledge/query', {
    operation: 'search',
    filters,
    pagination
  });
};

export const searchKnowledgeByText = async (text, agentId, limit = 10) => {
  return await makeAuthenticatedRequest('POST', '/knowledge/query', {
    operation: 'text_search',
    query: text,
    agent_id: agentId,
    limit
  });
};

export const getLearning = async (learningId) => {
  return await makeAuthenticatedRequest('POST', '/knowledge/query', {
    operation: 'get_learning',
    learning_id: learningId
  });
};

export const getRelatedLearnings = async (learningId) => {
  return await makeAuthenticatedRequest('POST', '/knowledge/query', {
    operation: 'get_related',
    learning_id: learningId
  });
};

export const updateLearning = async (learningId, updates, userId) => {
  return await makeAuthenticatedRequest('POST', '/knowledge/write', {
    operation: 'update_learning',
    learning_id: learningId,
    updates,
    user_id: userId
  });
};

// Dashboard/Analytics
export const getKnowledgeDashboard = async (agentId, days = 30) => {
  return await makeAuthenticatedRequest('POST', '/knowledge/analytics', {
    operation: 'get_dashboard',
    agent_id: parseInt(agentId),
    days
  });
};

// --- Agent Structure and Pending Executions ---

// Get mini agent tree structure
export const getMiniAgentTree = async (agentId) => {
  try {
    const response = await makeAuthenticatedRequest('POST', '/db/mini-agent-tree', { 
      agent_id: agentId 
    });
    
    // Use the extract logic
    return extractFromNestedResponse(response);
  } catch (error) {
    console.error('Error getting mini agent tree:', error);
    throw error;
  }
};

// Get agent pending executions
export const getAgentPendingExecutions = async (agentId) => {
  try {
    const response = await makeAuthenticatedRequest('POST', '/db/agent-pending-executions', { 
      agent_id: agentId 
    });
    
    return extractFromNestedResponse(response);
  } catch (error) {
    console.error('Error getting agent pending executions:', error);
    throw error;
  }
};

// Utility function to refresh micro-agent cache
export const refreshMicroAgentCache = async () => {
  try {
    console.log('Refreshing micro-agent cache...');
    microAgentCache.invalidate();
    await microAgentCache.initialize();
    console.log('Micro-agent cache refreshed successfully');
  } catch (error) {
    console.error('Error refreshing micro-agent cache:', error);
  }
};

// --- Cognito Admin API Functions ---

/**
 * List all users in the Cognito User Pool
 * @param {Object} options - Optional filters and pagination
 * @returns {Promise} Response with users list
 */
export const listCognitoUsers = async (options = {}) => {
  try {
    const payload = {
      action: 'list_users',
      limit: options.limit || 60,
      pagination_token: options.pagination_token || '',
      filter: options.filter || '',
      attributes_to_get: options.attributes_to_get || []
    };

    const response = await makeAuthenticatedRequest('POST', '/admin/cognito-admin/', payload);
    return response;
  } catch (error) {
    console.error('Error listing Cognito users:', error);
    throw error;
  }
};

/**
 * Get details for a specific Cognito user
 * @param {string} username - The username to fetch
 * @returns {Promise} Response with user details
 */
export const getCognitoUser = async (username) => {
  try {
    const payload = {
      action: 'get_user',
      username: username
    };

    const response = await makeAuthenticatedRequest('POST', '/admin/cognito-admin/', payload);
    return response;
  } catch (error) {
    console.error('Error getting Cognito user:', error);
    throw error;
  }
};

/**
 * Create a new Cognito user
 * @param {Object} userData - User data including username, email, etc.
 * @returns {Promise} Response with created user details
 */
export const createCognitoUser = async (userData) => {
  try {
    const payload = {
      action: 'create_user',
      username: userData.username,
      email: userData.email,
      given_name: userData.given_name || '',
      family_name: userData.family_name || '',
      phone_number: userData.phone_number || '',
      temporary_password: userData.temporary_password || '',
      send_invitation: userData.send_invitation !== false
    };

    const response = await makeAuthenticatedRequest('POST', '/admin/cognito-admin/', payload);
    return response;
  } catch (error) {
    console.error('Error creating Cognito user:', error);
    throw error;
  }
};

/**
 * Delete a Cognito user
 * @param {string} username - The username to delete
 * @returns {Promise} Response confirming deletion
 */
export const deleteCognitoUser = async (username) => {
  try {
    const payload = {
      action: 'delete_user',
      username: username
    };

    const response = await makeAuthenticatedRequest('POST', '/admin/cognito-admin/', payload);
    return response;
  } catch (error) {
    console.error('Error deleting Cognito user:', error);
    throw error;
  }
};

/**
 * Reset a user's password (admin action)
 * @param {string} username - The username to reset password for
 * @returns {Promise} Response confirming password reset
 */
export const resetCognitoUserPassword = async (username) => {
  try {
    const payload = {
      action: 'reset_password',
      username: username
    };

    const response = await makeAuthenticatedRequest('POST', '/admin/cognito-admin/', payload);
    return response;
  } catch (error) {
    console.error('Error resetting Cognito user password:', error);
    throw error;
  }
};

/**
 * Enable a Cognito user account
 * @param {string} username - The username to enable
 * @returns {Promise} Response confirming user enabled
 */
export const enableCognitoUser = async (username) => {
  try {
    const payload = {
      action: 'enable_user',
      username: username
    };

    const response = await makeAuthenticatedRequest('POST', '/admin/cognito-admin/', payload);
    return response;
  } catch (error) {
    console.error('Error enabling Cognito user:', error);
    throw error;
  }
};

/**
 * Disable a Cognito user account
 * @param {string} username - The username to disable
 * @returns {Promise} Response confirming user disabled
 */
export const disableCognitoUser = async (username) => {
  try {
    const payload = {
      action: 'disable_user',
      username: username
    };

    const response = await makeAuthenticatedRequest('POST', '/admin/cognito-admin/', payload);
    return response;
  } catch (error) {
    console.error('Error disabling Cognito user:', error);
    throw error;
  }
};

/**
 * Add a user to a Cognito group
 * @param {string} username - The username
 * @param {string} groupName - The group name to add user to
 * @returns {Promise} Response confirming user added to group
 */
export const addCognitoUserToGroup = async (username, groupName) => {
  try {
    const payload = {
      action: 'add_to_group',
      username: username,
      group_name: groupName
    };

    const response = await makeAuthenticatedRequest('POST', '/admin/cognito-admin/', payload);
    return response;
  } catch (error) {
    console.error('Error adding Cognito user to group:', error);
    throw error;
  }
};

/**
 * Remove a user from a Cognito group
 * @param {string} username - The username
 * @param {string} groupName - The group name to remove user from
 * @returns {Promise} Response confirming user removed from group
 */
export const removeCognitoUserFromGroup = async (username, groupName) => {
  try {
    const payload = {
      action: 'remove_from_group',
      username: username,
      group_name: groupName
    };

    const response = await makeAuthenticatedRequest('POST', '/admin/cognito-admin/', payload);
    return response;
  } catch (error) {
    console.error('Error removing Cognito user from group:', error);
    throw error;
  }
};

/**
 * List users in a specific Cognito group
 * @param {string} groupName - The group name to list users from
 * @param {Object} options - Optional pagination options
 * @returns {Promise} Response with users in the group
 */
export const listCognitoUsersInGroup = async (groupName, options = {}) => {
  try {
    const payload = {
      action: 'list_users_in_group',
      group_name: groupName,
      limit: options.limit || 60,
      next_token: options.next_token || ''
    };

    const response = await makeAuthenticatedRequest('POST', '/admin/cognito-admin/', payload);
    return response;
  } catch (error) {
    console.error('Error listing Cognito users in group:', error);
    throw error;
  }
};

// --- Cognito User API Functions (User-facing, non-admin) ---

/**
 * Get current user profile (authenticated)
 * @returns {Promise} Response with user profile data
 */
export const getUserProfile = async () => {
  try {
    const payload = {
      action: 'get_user'
    };

    const response = await makeAuthenticatedRequest('POST', '/cognito/cognito-user', payload);
    return response;
  } catch (error) {
    console.error('Error getting user profile:', error);
    throw error;
  }
};

/**
 * Update user attributes (authenticated)
 * @param {Object} attributes - Attributes to update (first_name, last_name, phone_number, email)
 * @returns {Promise} Response confirming update
 */
export const updateUserAttributes = async (attributes) => {
  try {
    const payload = {
      action: 'update_attributes',
      ...attributes
    };

    const response = await makeAuthenticatedRequest('POST', '/cognito/cognito-user', payload);
    return response;
  } catch (error) {
    console.error('Error updating user attributes:', error);
    throw error;
  }
};

/**
 * Change password (authenticated)
 * @param {string} previousPassword - Current password
 * @param {string} proposedPassword - New password
 * @returns {Promise} Response confirming password change
 */
export const changeUserPassword = async (previousPassword, proposedPassword) => {
  try {
    const payload = {
      action: 'change_password',
      previous_password: previousPassword,
      proposed_password: proposedPassword
    };

    const response = await makeAuthenticatedRequest('POST', '/cognito/cognito-user', payload);
    return response;
  } catch (error) {
    console.error('Error changing password:', error);
    throw error;
  }
};

/**
 * Reset password - initiate (unauthenticated)
 * @param {string} username - Username (email) to reset password for
 * @returns {Promise} Response confirming reset code sent
 */
export const resetUserPassword = async (username) => {
  try {
    const payload = {
      action: 'reset_password',
      username: username
    };

    const response = await makeUnauthenticatedRequest('POST', '/cognito/cognito-user', payload);
    return response;
  } catch (error) {
    console.error('Error resetting password:', error);
    throw error;
  }
};

/**
 * Confirm password reset (unauthenticated)
 * @param {string} username - Username (email)
 * @param {string} confirmationCode - Code received via email
 * @param {string} newPassword - New password
 * @returns {Promise} Response confirming password reset
 */
export const confirmResetUserPassword = async (username, confirmationCode, newPassword) => {
  try {
    const payload = {
      action: 'confirm_reset_password',
      username: username,
      confirmation_code: confirmationCode,
      new_password: newPassword
    };

    const response = await makeUnauthenticatedRequest('POST', '/cognito/cognito-user', payload);
    return response;
  } catch (error) {
    console.error('Error confirming password reset:', error);
    throw error;
  }
};

// Initialize the micro-agent cache on module load
(async () => {
  try {
    await microAgentCache.initialize();
    console.log('Micro-agent cache initialized successfully');
  } catch (error) {
    console.error('Failed to initialize micro-agent cache:', error);
  }
})();
